// approver-onboarding-modal/steps/WelcomeStep.tsx
import React, { useEffect, useCallback, useMemo } from 'react'
import { Typography, Button, Space, Tooltip } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { useApproverOnboardingContext } from '../context'
import { track } from '../../../services/analytics/analytics'
import { saveApproverOnboardingProgress } from '../../../util/approver-onboarding-storage'

import IntlMessages from '../../../util/IntlMessages'

const { Title, Text } = Typography

interface WelcomeStepProps {
  onExploreFirst?: () => void
}

// Maximum departments to show before collapsing into "and X more"
const MAX_DEPARTMENTS_TO_SHOW = 3

export const WelcomeStep: React.FC<WelcomeStepProps> = ({ onExploreFirst }) => {
  const { nextStep, departmentName, departmentNames, authUser, companyId, isLoading } = useApproverOnboardingContext()
  const { formatMessage } = useIntl()

  // Ensure we have a clean array of department names (filter out any undefined/null/empty values)
  const validDepartmentNames = useMemo(() => {
    if (!departmentNames || !Array.isArray(departmentNames)) {
      return []
    }
    return departmentNames.filter((name): name is string => Boolean(name && typeof name === 'string'))
  }, [departmentNames])

  // Track screen shown and initialize progress
  useEffect(() => {
    if (companyId && authUser) {
      track('APPROVER_ONBOARDING_WELCOME_SCREEN_SHOWN', {
        company_id: companyId,
        distinct_id: authUser.id,
        email: authUser.email,
        department: departmentName,
        departments: validDepartmentNames,
      })

      // Initialize progress when welcome step is shown (step 0)
      saveApproverOnboardingProgress(authUser.id, companyId, 0, departmentName, false)
    }
  }, [companyId, authUser, departmentName, validDepartmentNames])

  const handleStartSetup = useCallback(() => {
    if (companyId && authUser) {
      track('APPROVER_ONBOARDING_START_SETUP_CLICKED', {
        company_id: companyId,
        user_id: authUser.id,
      })
    }
    nextStep()
  }, [nextStep, companyId, authUser])

  const handleExploreFirst = useCallback(() => {
    if (companyId && authUser) {
      track('APPROVER_ONBOARDING_WELCOME_SCREEN_DISMISSED', {
        dismiss_method: 'EXPLORE_FIRST',
        company_id: companyId,
        user_id: authUser.id,
      })
    }
    if (onExploreFirst) {
      onExploreFirst()
    }
  }, [onExploreFirst, companyId, authUser])

  // Format department names display with tooltip for "and X more"
  const formattedDepartments = useMemo(() => {
    if (validDepartmentNames.length === 0) {
      return <Text className="welcome-title-highlight">{departmentName || 'Your Department'}</Text>
    }

    if (validDepartmentNames.length === 1) {
      return <Text className="welcome-title-highlight">{validDepartmentNames[0]}</Text>
    }

    if (validDepartmentNames.length === 2) {
      return (
        <>
          <Text className="welcome-title-highlight">{validDepartmentNames[0]}</Text>
          <Text className="welcome-title-separator"> {formatMessage({ id: 'app.and' })} </Text>
          <Text className="welcome-title-highlight">{validDepartmentNames[1]}</Text>
        </>
      )
    }

    if (validDepartmentNames.length === 3) {
      return (
        <>
          <Text className="welcome-title-highlight">{validDepartmentNames[0]}</Text>
          <Text className="welcome-title-separator">, </Text>
          <Text className="welcome-title-highlight">{validDepartmentNames[1]}</Text>
          <Text className="welcome-title-separator"> {formatMessage({ id: 'app.and' })} </Text>
          <Text className="welcome-title-highlight">{validDepartmentNames[2]}</Text>
        </>
      )
    }

    // More than 3 departments - show first 2 and "and X more" with tooltip
    const visibleDepartments = validDepartmentNames.slice(0, MAX_DEPARTMENTS_TO_SHOW - 1) // Show first 2
    const remainingDepartments = validDepartmentNames.slice(MAX_DEPARTMENTS_TO_SHOW - 1)
    const remainingCount = remainingDepartments.length

    const tooltipContent = (
      <div className="departments-tooltip">
        {remainingDepartments.map((dept, index) => (
          <div key={index}>{dept}</div>
        ))}
      </div>
    )

    return (
      <>
        {visibleDepartments.map((dept, index) => (
          <React.Fragment key={index}>
            <Text className="welcome-title-highlight">{dept}</Text>
            {index < visibleDepartments.length - 1 && <Text className="welcome-title-separator">, </Text>}
          </React.Fragment>
        ))}
        <Text className="welcome-title-separator"> {formatMessage({ id: 'app.and' })} </Text>
        <Tooltip title={tooltipContent} placement="bottom">
          <Text className="welcome-title-highlight welcome-title-more">
            <IntlMessages id="approver.onboarding.welcome.departments.andMore" values={{ count: remainingCount }} />
          </Text>
        </Tooltip>
      </>
    )
  }, [validDepartmentNames, departmentName, formatMessage])

  // Description text based on number of departments
  const descriptionValues = useMemo(() => {
    if (validDepartmentNames.length <= 1) {
      return { departmentName: validDepartmentNames[0] || departmentName || 'Your Department' }
    }
    // For multiple departments, we use a different translation key
    return { count: validDepartmentNames.length }
  }, [validDepartmentNames, departmentName])

  return (
    <div className="approver-onboarding-step welcome-step">
      <div className="step-content">
        <div className="step-left-section">
          <div className="welcome-content-container">
            {/* VT Logo at top */}
            <img src="/logo512.png" alt="Vacation Tracker Logo" className="welcome-logo" />

            <Title className="welcome-title">
              <IntlMessages id="approver.onboarding.welcome.title" />
              {formattedDepartments}
            </Title>

            <Text className="welcome-description">
              {validDepartmentNames.length > 1 ? (
                <IntlMessages id="approver.onboarding.welcome.description.multiple" values={descriptionValues} />
              ) : (
                <IntlMessages id="approver.onboarding.welcome.description" values={descriptionValues} />
              )}
            </Text>

            {/* Setup checklist */}
            <div className="welcome-checklist">
              <Space direction="vertical" size="small">
                <div className="welcome-checklist-item">
                  <CheckOutlined className="welcome-checklist-icon" />
                  <Text>
                    <IntlMessages id="approver.onboarding.welcome.checklist.item1" />
                  </Text>
                </div>
                <div className="welcome-checklist-item">
                  <CheckOutlined className="welcome-checklist-icon" />
                  <Text>
                    <IntlMessages id="approver.onboarding.welcome.checklist.item2" />
                  </Text>
                </div>
                <div className="welcome-checklist-item">
                  <CheckOutlined className="welcome-checklist-icon" />
                  <Text>
                    <IntlMessages id="approver.onboarding.welcome.checklist.item3" />
                  </Text>
                </div>
              </Space>
            </div>

            <div className="welcome-actions">
              <Button type="primary" size="large" onClick={handleStartSetup} loading={isLoading} className="welcome-primary-button">
                <IntlMessages id="approver.onboarding.welcome.startTour" />
              </Button>

              <Button type="link" onClick={handleExploreFirst} className="welcome-link-button">
                <IntlMessages id="approver.onboarding.welcome.exploreLater" />
              </Button>
            </div>
          </div>
        </div>

        <div className="step-right-section">
          <div className="welcome-screenshots-container">
            {/* Stacked screenshots container */}
            <div className="welcome-stacked-container">
              {/* Welcome2Svg (back layer - top right) */}
              <div className="welcome-screenshot-back">
                <img src="/approver-onboarding/Welcome-2.svg" alt="Welcome screenshot" className="welcome-screenshot" />
              </div>

              {/* Welcome1Svg (front layer - bottom left) */}
              <div className="welcome-screenshot-front">
                <img src="/approver-onboarding/Welcome-1.svg" alt="Welcome screenshot" className="welcome-screenshot" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
